/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import HPITemplateForm from "components/forms/HPITemplateForm";
import Button from "components/generals/Button";
import DefaultCell from "components/generals/DefaultCell";
import { Modal } from "components/generals/Modal";
import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";
import DataTable from "dashboard-template/examples/Tables/DataTable";
import { TableProps } from "interfaces";
import { HeaderEnum, TemplateInterface } from "medicalsystem-shared";
import { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { EntityPermissions, FormActionType, PermissionsUser } from "types";
import { getNewHPITemplateDataContent } from "utils/model.utils";
import Box from "components/generals/Box";
import ButtonDialog from "components/generals/ButtonDialog";
import PermissionWrapper from "components/generals/PermissionWrapper";
import { TemplateTypeEnum } from "medicalsystem-shared";
import { TagsInterface } from "interfaces";

/**
 * Extended properties for the HPITemplateTable component.
 * 
 * @interface HPITemplateTableProps
 * @extends {TableProps<TemplateInterface>}
 * @property {TagsInterface[]} [filterTag] - Array of tags to filter HPI templates by
 */
interface HPITemplateTableProps extends TableProps<TemplateInterface> {
  filterTag?: TagsInterface[];
}

/**
 * HPI (History of Present Illness) template table component.
 * Specialized table for managing HPI clinical documentation templates.
 * 
 * Features:
 * - Displays HPI template information in sortable data table
 * - Filters templates by TemplateTypeEnum.HPI type
 * - Columns: title, version, tags, action buttons
 * - View/edit template functionality via modal with HPITemplateForm
 * - Delete template with confirmation dialog and list update
 * - Optional select function for template selection workflow
 * - Read-only mode support
 * - Tags display with array-to-string conversion
 * - Permission-based access control
 * - Tag-based filtering support
 * 
 * HPI Template Content:
 * - Chief complaint
 * - Symptom onset and duration
 * - Symptom characteristics (quality, severity, location)
 * - Alleviating/aggravating factors
 * - Associated symptoms
 * - Previous episodes
 * - Treatment history
 * 
 * Filtering:
 * - Automatically filters modelList to only HPI type templates
 * - Additional tag filtering via filterTag prop
 * 
 * Security:
 * - Uses HeaderEnum.RESOURCE_ID for secure identification
 * - Permission-based rendering via PermissionWrapper
 * - Validates HPI_PAGE permissions before operations
 * 
 * @param {HPITemplateTableProps} props - Table properties
 * @param {TemplateInterface[]} props.modelList - Array of all templates (will be filtered to HPI)
 * @param {Function} props.setModelList - Callback to update template list
 * @param {Function} [props.selectFunction] - Optional callback for template selection
 * @param {boolean} [props.readOnlyValue] - Whether table is in read-only mode
 * @param {TagsInterface[]} [props.filterTag] - Tags to filter templates by
 * 
 * @returns {JSX.Element} HPI template table with type-specific filtering
 */
const HPITemplateTable: FC<HPITemplateTableProps> = ({
  modelList,
  setModelList,
  selectFunction,
  readOnlyValue,
  filterTag,
  ...props
}) => {
  const [selectedHPITemplate, setSelectedHPITemplate] = useState<TemplateInterface>(
    getNewHPITemplateDataContent()
  );
  const [activeEditHPITemplateModal, setActiveEditHPITemplateModal] = useState<boolean>(false);
  const [modalAction, setModalAction] = useState<FormActionType>();
  const { t } = useTranslation();
  const hpiTemplates = modelList.filter(
    (template) => template.type.trim().toUpperCase() == TemplateTypeEnum.HPI
  );
  const getUITable = (list: TemplateInterface[]) => {
    return {
      columns: [
        {
          Header: t("hpitemplateTable.title"),
          accessor: "title",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("hpitemplateTable.version"),
          accessor: "version",
          sortIndicatorType: "numeric",
          Cell: ({ value }: any) => <DefaultCell value={value} />,
        },
        {
          Header: t("hpitemplateTable.tags"),
          accessor: "tags",
          sortIndicatorType: "az",
          Cell: ({ value }: any) => {
            const displayValue = Array.isArray(value) ? value.join(", ") : value;
            return <DefaultCell value={displayValue} />;
          },
        },
        {
          Header: t("hpitemplateTable.action"),
          accessor: "id",
          disableSortBy: true,
          Cell: ({ value }: any) => (
            <>
              <Button
                id="cy-view-hpi"
                sx={{ marginRight: 2 }}
                variant="contained"
                color="info"
                onClick={() => {
                  const hpitemplate = list.find((p) => p.id === value);
                  if (hpitemplate) {
                    setModalAction(FormActionType.MODIFY);
                    openModalHandler(hpitemplate);
                  }
                }}
              >
                {t("hpitemplateTable.view")}
              </Button>
              {!readOnlyValue ? (
                <PermissionWrapper
                  requiredPermissions={[PermissionsUser[EntityPermissions.HPI_PAGE].DELETE]}
                >
                  <ButtonDialog
                    onAcceptHandle={() => {
                      const hpitemplate = list.find((p) => p.id === value);
                      if (hpitemplate) {
                        deleteHandler(hpitemplate);
                      }
                    }}
                    buttonText={t("hpitemplateTable.deleteAction")}
                    title={t("hpitemplateTable.deleteConfirmation")}
                    description={t("hpitemplateTable.description")}
                    labelledbyID={"HpiTemplateDelete"}
                    describedbyID={"HpiTemplateDescriptionId"}
                  />
                </PermissionWrapper>
              ) : (
                <Button
                  id="cy-select-hpi"
                  sx={{ marginLeft: 2 }}
                  variant="contained"
                  color="info"
                  onClick={() => {
                    const hpitemplate = list.find((p) => p.id === value);
                    if (hpitemplate) {
                      selectFunction && selectFunction(hpitemplate);
                    }
                  }}
                >
                  {t("hpitemplateTable.selectAction")}
                </Button>
              )}
            </>
          ),
        },
      ],

      rows: list,
    };
  };
  const dataTableData = useMemo(() => getUITable(hpiTemplates), [modelList]);
  const [_isLoading, _error, sendRequest] = useHttp();

  const openModalHandler = (hpitemplate: TemplateInterface) => {
    setSelectedHPITemplate(hpitemplate);
    setActiveEditHPITemplateModal(true);
  };

  const deleteHandler = (hpitemplate: TemplateInterface) => {
    setSelectedHPITemplate(hpitemplate);
    sendRequest<boolean, TemplateInterface>(
      CoreAPI_URLs.HPI_TEMPLATE_GENERAL_OPERATIONS,
      onHPITemplateDeleteHandler,
      "DELETE",
      JSON.stringify(hpitemplate),
      [{ header: HeaderEnum.RESOURCE_ID, value: hpitemplate.id }],
      { payload: hpitemplate }
    );
  };

  const onHPITemplateDeleteHandler = (result: boolean, hpitemplate: TemplateInterface) => {
    const indexList = modelList.indexOf(hpitemplate);
    modelList.splice(indexList, 1);
    setModelList([...modelList]);
  };

  return (
    <>
      <Box mt={2} mb={2} width="100%" display="flex" justifyContent="flex-start">
        <PermissionWrapper requiredPermissions={[PermissionsUser[EntityPermissions.HPI_PAGE].VIEW]}>
          <Button
            type="button"
            variant="contained"
            color="primary"
            onClick={() => {
              setModalAction(FormActionType.NEW);
              setSelectedHPITemplate(getNewHPITemplateDataContent);
              setActiveEditHPITemplateModal(true);
            }}
          >
            {t("hpitemplatesDataContent.newHPITemplate")}
          </Button>
        </PermissionWrapper>
      </Box>
      <DataTable {...props} table={dataTableData} canSearch />
      <Modal
        open={activeEditHPITemplateModal}
        onClose={() => {
          setActiveEditHPITemplateModal(false);
        }}
      >
        <HPITemplateForm
          readOnlyValue={readOnlyValue}
           canSearchPlaceholder={t("hpitemplateTable.search")}
          modelList={modelList}
          setModelList={setModelList}
          action={modalAction}
          model={selectedHPITemplate}
          onCloseAction={() => {
            setActiveEditHPITemplateModal(false);
          }}
          isModal={true}
          filterTag={filterTag}
        />
      </Modal>
    </>
  );
};

export default HPITemplateTable;
